/**
 * Hybrid ECC + Kyber Encryption Helper for Client-Side
 *
 * This module provides functions to encrypt data using the hybrid public key
 * from the enclave before sending it securely.
 *
 * Uses:
 * - ECC (P-256) via Web Crypto API for ECDH
 * - ML-KEM-768 (Kyber768) via crystals-kyber-js for post-quantum encryption
 * - AES-256-GCM for symmetric encryption
 */
import { MlKem768 } from 'crystals-kyber-js';

/**
 * Fetch the current enclave public key
 */
export async function fetchEnclavePublicKey() {
    try {
        const response = await fetch('/api/enclave/public-key');
        const result = await response.json();

        if (!result.success) {
            throw new Error(result.error || 'Failed to fetch public key');
        }

        return result.data;
    } catch (error) {
        console.error('Error fetching enclave public key:', error);
        throw error;
    }
}

/**
 * Parse PEM-encoded ECC public key
 */
function parseEccPublicKeyPem(pem) {
    // Remove PEM headers and decode base64
    const base64 = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s/g, '');

    return base64;
}

/**
 * Encrypt data using hybrid ECC + Kyber encryption
 *
 * @param {Object} data - The data to encrypt
 * @param {Object} publicKey - The enclave public key object
 * @returns {Promise<string>} Base64-encoded encrypted payload
 */
export async function encryptForEnclave(data, publicKey) {
    if (!publicKey) {
        throw new Error('Public key is required');
    }

    // Serialize the data
    const plaintext = JSON.stringify(data);
    const plaintextBytes = new TextEncoder().encode(plaintext);

    // Step 1: Generate ephemeral ECC keypair for ECDH
    const ephemeralKeyPair = await generateEphemeralEccKeyPair();

    // Step 2: Perform ECDH with enclave's ECC public key
    const eccSharedSecret = await performEcdh(
        ephemeralKeyPair.privateKey,
        publicKey.ecc_public_key_pem
    );

    // Step 3: Kyber encapsulation (produces shared secret + ciphertext)
    // NOTE: This requires a Kyber library. For now, we'll use a placeholder.
    // In production, use: import { kyber768 } from 'kyber-crystals-js';
    const kyberResult = await performKyberEncapsulation(
        publicKey.kyber_public_key_base64
    );

    // Step 4: Combine both shared secrets using HKDF
    const hybridSecret = await deriveHybridKey(
        eccSharedSecret,
        kyberResult.sharedSecret
    );

    // Step 5: Encrypt the data using AES-GCM with the hybrid secret
    const encrypted = await encryptWithAesGcm(plaintextBytes, hybridSecret);

    // Step 6: Package everything together
    const payload = {
        version: '1.0',
        ecc_ephemeral_public_key: await exportPublicKey(ephemeralKeyPair.publicKey),
        kyber_ciphertext: kyberResult.ciphertext,
        encrypted_data: arrayBufferToBase64(encrypted.ciphertext),
        iv: arrayBufferToBase64(encrypted.iv),
        auth_tag: arrayBufferToBase64(encrypted.authTag),
        timestamp: Date.now()
    };

    return JSON.stringify(payload);
}

/**
 * Generate ephemeral ECC keypair for ECDH
 */
async function generateEphemeralEccKeyPair() {
    const keyPair = await crypto.subtle.generateKey(
        {
            name: 'ECDH',
            namedCurve: 'P-256'
        },
        true,
        ['deriveBits']
    );

    return keyPair;
}

/**
 * Perform ECDH key agreement
 */
async function performEcdh(privateKey, publicKeyPem) {
    // Import the enclave's public key
    const publicKeyBase64 = parseEccPublicKeyPem(publicKeyPem);
    const publicKeyBytes = base64ToArrayBuffer(publicKeyBase64);

    const importedPublicKey = await crypto.subtle.importKey(
        'spki',
        publicKeyBytes,
        {
            name: 'ECDH',
            namedCurve: 'P-256'
        },
        false,
        []
    );

    // Derive shared secret
    const sharedSecret = await crypto.subtle.deriveBits(
        {
            name: 'ECDH',
            public: importedPublicKey
        },
        privateKey,
        256
    );

    return new Uint8Array(sharedSecret);
}

/**
 * Perform ML-KEM-768 (Kyber768) encapsulation
 *
 * Uses the crystals-kyber-js library to perform real post-quantum encapsulation.
 * This generates a shared secret and ciphertext that only the enclave can decrypt.
 */
async function performKyberEncapsulation(publicKeyBase64) {
    try {
        // Convert base64 public key to Uint8Array
        const publicKeyBytes = base64ToUint8Array(publicKeyBase64);

        // Create ML-KEM-768 instance and perform encapsulation
        const kyber = new MlKem768();
        const [ciphertext, sharedSecret] = await kyber.encap(publicKeyBytes);

        console.log('[Kyber] ✓ Real ML-KEM-768 encapsulation performed');

        return {
            sharedSecret: sharedSecret,
            ciphertext: arrayBufferToBase64(ciphertext)
        };
    } catch (error) {
        console.error('[Kyber] Encapsulation failed:', error);
        throw new Error('Kyber encapsulation failed: ' + error.message);
    }
}

/**
 * Utility: Convert base64 to Uint8Array
 */
function base64ToUint8Array(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/**
 * Derive hybrid encryption key from both shared secrets using HKDF
 */
async function deriveHybridKey(eccSecret, kyberSecret) {
    // Concatenate both secrets
    const combined = new Uint8Array(eccSecret.length + kyberSecret.length);
    combined.set(eccSecret, 0);
    combined.set(kyberSecret, eccSecret.length);

    // Import as raw key material
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        combined,
        'HKDF',
        false,
        ['deriveBits']
    );

    // Derive final encryption key using HKDF
    const derivedBits = await crypto.subtle.deriveBits(
        {
            name: 'HKDF',
            hash: 'SHA-256',
            salt: new Uint8Array(32), // In production, use a proper salt
            info: new TextEncoder().encode('ocuros-hybrid-encryption')
        },
        keyMaterial,
        256
    );

    return new Uint8Array(derivedBits);
}

/**
 * Encrypt data using AES-256-GCM
 */
async function encryptWithAesGcm(plaintext, key) {
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key,
        'AES-GCM',
        false,
        ['encrypt']
    );

    const encrypted = await crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: iv,
            tagLength: 128
        },
        cryptoKey,
        plaintext
    );

    // AES-GCM returns ciphertext with auth tag appended
    const ciphertext = new Uint8Array(encrypted.slice(0, encrypted.byteLength - 16));
    const authTag = new Uint8Array(encrypted.slice(encrypted.byteLength - 16));

    return {
        ciphertext,
        iv,
        authTag
    };
}

/**
 * Export public key to base64
 */
async function exportPublicKey(publicKey) {
    const exported = await crypto.subtle.exportKey('spki', publicKey);
    return arrayBufferToBase64(exported);
}

/**
 * Utility: Convert ArrayBuffer to base64
 */
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Utility: Convert base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

/**
 * Example usage function
 */
export async function exampleEncryptOrder(orderData) {
    try {
        // 1. Fetch the enclave's public key
        const publicKey = await fetchEnclavePublicKey();
        console.log('Fetched enclave public key:', publicKey);

        // 2. Encrypt the order data
        const encryptedPayload = await encryptForEnclave(orderData, publicKey);
        console.log('Encrypted payload:', encryptedPayload);

        // 3. Send to the enclave endpoint
        const response = await fetch('/api/enclave/encrypt', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
                encrypted_payload: encryptedPayload,
                submission_type: 'order'
            })
        });

        const result = await response.json();

        if (!result.success) {
            throw new Error(result.error || 'Encryption submission failed');
        }

        console.log('Successfully submitted encrypted data:', result);
        return result;

    } catch (error) {
        console.error('Error encrypting and submitting order:', error);
        throw error;
    }
}
